import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { AccountingInterfaceModel } from 'src/app/models/invoicing/accounting-interface.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { AccountingInterfaceDetailCaptureTableModel } from 'src/app/models/tables/invoicing/accounting-interface-detail-capture.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { AccountingInterfaceService } from 'src/app/services/invoicing/accounting-interface.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { AccountingInterfacePreviewComponent } from '../accounting-interface-preview/accounting-interface-preview.component';


@Component({
  selector: 'app-accounting-interface-detail',
  templateUrl: './accounting-interface-detail.component.html',
  styleUrls: ['./accounting-interface-detail.component.scss'],
  standalone: false
})
export class AccountingInterfaceDetailComponent implements OnInit {

  private readonly accountingInterfaceService = inject(AccountingInterfaceService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @ViewChild('documentsSelection') documentSelectionModalComponent?: CustomModalComponent | null = null;
  @ViewChild('preview') previewModalComponent?: CustomModalComponent | null = null;

  @Input('accountingInterfaceModel')
  set changeInterface(changeInterface: AccountingInterfaceModel) {
    this.changeAccountingInterface(changeInterface);
  }

  @Input('disabledActions')
  set changeDisable(disable: boolean) {
    this.tableActions = disable ? [] : this.getDefaultActions();
  }

  @Input() typeInterface: string = '';
  @Input() permissionUpdatable: boolean = false;
  @Input() visiblePreview: boolean = false;
  @Input() siesaSynchronization: boolean = false;
  @Output() emitQuantityDetails = new EventEmitter<number>();

  accountingInterfaceContract: AccountingInterfaceModel = new AccountingInterfaceModel();
  ascendant: boolean = true;
  ascendantDocument: boolean = true;
  columnSort: string | null = null;
  columnSortDocument: string | null = null;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  componentTableSelection = CustomTableSelectComponent;
  componentPreview = AccountingInterfacePreviewComponent;
  dataDocument: PagedResponseModel<AccountingInterfaceDetailCaptureTableModel> =
    new PagedResponseModel<AccountingInterfaceDetailCaptureTableModel>();
  detailData: PagedResponseModel<AccountingInterfaceDetailCaptureTableModel> =
    new PagedResponseModel<AccountingInterfaceDetailCaptureTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  disabledButton: boolean = false;
  disabledPreview: boolean = false;
  filtersDocument: ColumnResponseModel[] = [];
  headerDocument: ColumnResponseModel[] = [];
  inputsForDocumentsSelection$ = new BehaviorSubject<any>({
    filterPersistentKey: 'accounting-interface-add-documents',
    isFilterPersistent: true,
    headers: [],
    data: [],
    showCheckbox: false,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
  });
  inputsForPreview$ = new BehaviorSubject<any>({
    accountingInterfaceModel: new AccountingInterfaceModel,
    quantityDetails: 0,
    siesaSynchronization: false
  });
  limit: number = 10;
  limitDocument: number = 10;
  output$ = new Subject<any>();
  outputForPreview$ = new Subject<any>();
  page: number = 1;
  pageDocument: number = 1;
  quantityDetails: number = 0;
  subscription: Subscription;
  tableActions: TableAction[] = [];

  ngOnInit() {
    this.getColumns(false);
    this.tableActions = this.getDefaultActions();

    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeDocument(payload);
          break;
        case 'onFilterChange':
          this.onFilterChange(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  changeAccountingInterface(changeInterface: AccountingInterfaceModel) {
    this.accountingInterfaceContract = changeInterface;
    if (this.accountingInterfaceContract.accountingInterfaceId != 0) {
      this.clearSelectionTrigger++;
      this.getAccountingInterfaceDetailById();

      this.page = 1;
      this.disabledPreview = false;
      this.disabledButton = !this.accountingInterfaceContract.active;
    } else {
      this.detailData = new PagedResponseModel<AccountingInterfaceDetailCaptureTableModel>();
      this.disabledButton = true;
      this.disabledPreview = true;
    }
  }

  async deleteAccountingInterfaceDetail(selectedIds: number[]) {
    if (selectedIds.length === 0) return;
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar los documentos seleccionados?')) {
      try {
        await firstValueFrom(this.accountingInterfaceService.removeAccountingInterfaceDetails(this.accountingInterfaceContract.accountingInterfaceId, selectedIds));
        this.toastHandlerService.showToast(
          'success',
          'Documentos eliminados exitosamente.'
        );
        this.clearSelectionTrigger++;
        this.getAccountingInterfaceDetailById();
      } catch (error) {
        console.error('Error al eliminar el documento de la interfaz .', error);
      }
    }
  }

  async getAccountingInterfaceDetailById() {
    try {
      this.detailData = await firstValueFrom(this.accountingInterfaceService.getAccountingInterfaceDetailById(this.accountingInterfaceContract.accountingInterfaceId, this.page, this.limit, this.columnSort, this.ascendant,));
      this.quantityDetails = this.detailData.totalItems;
      this.emitQuantityDetails.emit(this.detailData.totalItems);
      this.updateInputsForPreview();
    } catch (error) {
      console.error('Error al buscar los los documentos de la interfaz.', error);
    }
  }

  async getColumns(isFilter: boolean) {
    try {
      this.detailHeader = await firstValueFrom(this.accountingInterfaceService.getColumnsForDetailCapture(isFilter));
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    return [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        action: 'edit'
      }
    ];
  }

  async getListDocuments() {
    try {
      this.dataDocument = await firstValueFrom(this.accountingInterfaceService.getAccountingInterfaceDetailForSelection(
        this.accountingInterfaceContract.accountingInterfaceId, this.pageDocument, this.limitDocument,
        this.columnSortDocument, this.ascendantDocument, this.filtersDocument));
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de los documentos', error);
    }
  }

  async handleSelectedItem(selectedItem: any) {
    if (selectedItem.length > 0) {
      try {
        const response = await firstValueFrom(this.accountingInterfaceService.addAccountingInterfaceDetails(this.accountingInterfaceContract.accountingInterfaceId, selectedItem));
        if (response.addDetailWithError) {
          this.toastHandlerService.showToast(
            'warning',
            'Algunos documentos no se agregaron porque no están en estado impresos o pagados. Por favor revise el detalle de la interfaz.',
          );
        }
        this.getAccountingInterfaceDetailById();
      } catch (error) {
        console.error('Hubo problemas al agregar los documentos a la interfaz', error)
      }
    }
  }

  async onAction(event: { action: string, item?: AccountingInterfaceDetailCaptureTableModel, selectedItems?: number[] }) {
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteAccountingInterfaceDetail(event.selectedItems);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getAccountingInterfaceDetailById();
  }

  async openDocumentSelectionModal() {
    this.filtersDocument = [];
    this.updateInputs();
    await this.getColumns(true);
    this.headerDocument = this.detailHeader;
    await this.getListDocuments();
    this.documentSelectionModalComponent && this.documentSelectionModalComponent.show();
  }

  async onPageChangeDocument(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageDocument = event.page;
    this.limitDocument = event.pageSize;
    this.columnSortDocument = event.sortField || null;
    this.ascendantDocument = event.sortOrder === 1;
    this.getListDocuments();
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filtersDocument = this.removeDuplicates(filters);
    this.filtersDocument.forEach((filter: any) => this.processFilter(filter));
    this.getListDocuments();
  }

  openPreviewModal() {
    this.previewModalComponent && this.previewModalComponent.show();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersDocument = this.filtersDocument.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersDocument.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  updateInputs() {
    this.inputsForDocumentsSelection$.next({
      filterPersistentKey: 'accounting-interface-add-documents',
      isFilterPersistent: true,
      headers: this.headerDocument,
      data: this.dataDocument,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      filters: this.filtersDocument,
    });
  }

  updateInputsForPreview() {
    this.inputsForPreview$.next({
      accountingInterfaceModel: this.accountingInterfaceContract,
      quantityDetails: this.quantityDetails,
      siesaSynchronization: this.siesaSynchronization
    });
  }
}
